// Episodes screen — ported from Claude Design/app/episodes.jsx
// Adapted to react-router + API store.

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../state/store.js';
import { api } from '../lib/api.js';
// note: `api` is imported but the EpisodeCard component needs its own reference
import { VideoThumb, Icons, StatusLabel, ruPlural } from '../components/ui.jsx';
import { formatDurationHm, formatBytes, formatDateRu, relativeTime } from '../lib/format.js';

export function EpisodesScreen() {
  const navigate = useNavigate();
  const episodes = useStore((s) => s.episodes);
  const loadEpisodes = useStore((s) => s.loadEpisodes);
  const toast = useStore((s) => s.toast);

  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [uploading, setUploading] = useState(null);
  const [fbModal, setFbModal] = useState(null); // { url, title } when open

  useEffect(() => { loadEpisodes(); }, [loadEpisodes]);

  // Episodes vs standalone reels — for now we treat all `file_upload` short ones
  // (no chapters) as "reels", everything else as episodes.
  const reels = episodes.filter((e) => e.source_kind === 'file_upload' && (e.duration_seconds || 0) < 6 * 60);
  const eps = episodes.filter((e) => !reels.includes(e));

  const startUpload = async (file) => {
    if (!file) return;
    setUploading({ filename: file.name, progress: 0 });
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      const res = await api.uploadEpisode(fd);
      toast(`«${file.name}» загружен · ИИ начал анализ`, 'ok');
      setUploading(null);
      loadEpisodes();
    } catch (err) {
      toast(`ошибка загрузки: ${err.message}`, 'err');
      setUploading(null);
    }
  };

  const startFb = () => {
    // Default title — today as DD.MM.YY. Backend will overwrite with the FB
    // upload_date if the user leaves this as-is, so this is just a sensible
    // placeholder for the modal.
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    setFbModal({ url: '', title: `Meditación ${dd}.${mm}.${yy}` });
  };

  const submitFb = async () => {
    const { url, title } = fbModal || {};
    if (!url?.trim()) { toast('нужен URL', 'warn'); return; }
    try {
      await api.episodeFromFb({ url: url.trim(), title: title?.trim() || null });
      toast('эпизод создан · yt-dlp начнёт скачивать через несколько секунд', 'ok');
      setFbModal(null);
      loadEpisodes();
    } catch (err) {
      toast(`ошибка: ${err.message}`, 'err');
    }
  };

  const renameEpisode = async (id, newTitle) => {
    try {
      const updated = await api.updateEpisode(id, { title: newTitle });
      toast('переименовано', 'ok');
      loadEpisodes();
      return updated;
    } catch (err) {
      toast(err.message, 'err');
    }
  };

  const open = (e) => {
    if (e.status !== 'ready') {
      toast(
        e.status === 'pending' || e.status === 'downloading' || e.status === 'transcribing' || e.status === 'analyzing'
          ? 'эпизод ещё обрабатывается'
          : 'обработка не завершилась — попробуй позже',
        'warn',
      );
      return;
    }
    navigate(`/episode/${e.id}`);
  };

  const filtered = eps.filter((e) =>
    filter === 'all' ? true :
    filter === 'ready' ? e.status === 'ready' :
    e.status !== 'ready' && e.status !== 'failed'
  );

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'dur') return (b.duration_seconds || 0) - (a.duration_seconds || 0);
    if (sort === 'stories') return (b.clips_count || 0) - (a.clips_count || 0);
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const totalStories = eps.reduce((a, e) => a + (e.clips_count || 0), 0);

  return (
    <div className="screen">
      {/* Standalone Reels — compact section */}
      <div className="screen-header screen-header-compact">
        <h1 className="page-title">Рилсы</h1>
        <span className="page-sub">короткие 9:16 ролики, загруженные отдельно</span>
      </div>

      <div className="reel-grid">
        <UploadCard
          kind="reel"
          uploading={uploading}
          onPick={startUpload}
          onClear={() => setUploading(null)}
        />
        {reels.map((r) => <ReelCard key={r.id} r={r} onOpen={() => open(r)} />)}
      </div>

      {/* Episodes section */}
      <div className="screen-header screen-header-compact" style={{ marginTop: 28 }}>
        <h1 className="page-title">Эпизоды</h1>
        <span className="page-sub"><b>{eps.length}</b> записей · <b>{totalStories}</b> фрагментов</span>
        <div className="grow" />
        <div className="screen-header-actions">
          <select className="select" value={filter} onChange={(e) => setFilter(e.target.value)} title="фильтр по статусу">
            <option value="all">все эпизоды</option>
            <option value="ready">готовые</option>
            <option value="processing">в обработке</option>
          </select>
          <select className="select" value={sort} onChange={(e) => setSort(e.target.value)} title="сортировка">
            <option value="recent">сначала свежие</option>
            <option value="dur">по длительности</option>
            <option value="stories">по числу фрагментов</option>
          </select>
        </div>
      </div>

      <div className="ep-grid">
        <UploadCard
          kind="episode"
          uploading={uploading}
          onPick={startUpload}
          onClear={() => setUploading(null)}
          onFb={startFb}
        />
        {sorted.map((e) => <EpisodeCard key={e.id} e={e} onOpen={() => open(e)} onRename={renameEpisode} />)}
      </div>

      {fbModal && (
        <FbImportModal
          value={fbModal}
          onChange={(patch) => setFbModal((cur) => ({ ...cur, ...patch }))}
          onSubmit={submitFb}
          onCancel={() => setFbModal(null)}
        />
      )}
    </div>
  );
}

function FbImportModal({ value, onChange, onSubmit, onCancel }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="hand-h3">Импорт с Facebook</h3>
          <button className="iconbtn" onClick={onCancel} title="закрыть">✕</button>
        </div>
        <label className="modal-field">
          <span className="modal-label">URL трансляции / архива</span>
          <input
            className="text-input"
            type="url"
            placeholder="https://www.facebook.com/dosmundos.pe/videos/..."
            autoFocus
            value={value.url}
            onChange={(e) => onChange({ url: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter' && value.url.trim()) onSubmit(); }}
          />
        </label>
        <label className="modal-field">
          <span className="modal-label">Название эпизода</span>
          <input
            className="text-input"
            type="text"
            value={value.title}
            onChange={(e) => onChange({ title: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter' && value.url.trim()) onSubmit(); }}
          />
          <span className="modal-hint">
            если оставить как есть — после скачивания подтянется дата из FB-поста
          </span>
        </label>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel}>отмена</button>
          <button className="btn btn-dark" onClick={onSubmit} disabled={!value.url.trim()}>
            скачать с FB
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadCard({ kind, uploading, onPick, onClear, onFb }) {
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const isReel = kind === 'reel';

  return (
    <div
      className={'upload-card ' + (isReel ? 'upload-card-reel ' : '') + (dragOver ? 'drag ' : '') + (uploading ? 'uploading' : '')}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onPick(f);
      }}
      onClick={() => !uploading && fileRef.current?.click()}
    >
      <input ref={fileRef} type="file" accept="video/*,audio/*" style={{ display: 'none' }}
             onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
      {!uploading ? (
        <>
          <div className="upload-icon">{Icons.upload}</div>
          <div className="upload-title" style={isReel ? { fontSize: 20 } : undefined}>
            Загрузить <em>{isReel ? 'рилс' : 'эпизод'}</em>
          </div>
          <div className="upload-sub">
            {isReel
              ? 'отдельный 9:16 ролик до 5 мин · mp4 · mov'
              : <>перетащи видео или аудио сюда.<br/>mp4 · mov · mp3 · wav — до 5 ГБ</>}
          </div>
          <div className="upload-actions">
            <button className="btn btn-dark" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>выбрать файл</button>
            {!isReel && onFb && (
              <button className="btn btn-outline" onClick={(e) => { e.stopPropagation(); onFb(); }}>FB-ссылка</button>
            )}
          </div>
          {!isReel && <div className="upload-storage">FB cookies подтянутся из VNC</div>}
        </>
      ) : (
        <div className="upload-progress">
          <div className="upload-progress-title">Загружаю «{uploading.filename}»</div>
          <div className="ringbar"><div className="ringbar-fill" style={{ width: uploading.progress + '%' }} /></div>
          <div className="upload-progress-sub">{Math.round(uploading.progress)}%</div>
          <button className="btn-link" onClick={(e) => { e.stopPropagation(); onClear(); }}>отменить</button>
        </div>
      )}
    </div>
  );
}

function EpisodeCard({ e, onOpen, onRename }) {
  const status = e.status;
  const isProcessing = ['pending', 'downloading', 'transcribing', 'analyzing'].includes(status);
  const isReady = status === 'ready';
  const isFailed = status === 'failed';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(e.title);
  useEffect(() => { setDraft(e.title); }, [e.title]);

  const commitRename = async () => {
    const t = draft.trim();
    setEditing(false);
    if (!t || t === e.title) return;
    await onRename?.(e.id, t);
  };

  return (
    <div className={'ep-card' + (isProcessing ? ' ep-card-busy' : '')} onClick={onOpen} role="button">
      <div className="ep-thumb">
        <VideoThumb aspect="16/9" src={e.thumbnail_url}
          label={isReady ? `${e.title}` : (isProcessing ? 'обработка…' : 'не готов')} />
        {e.duration_seconds && <span className="badge badge-dark">{formatDurationHm(e.duration_seconds)}</span>}
        {isProcessing && <span className="badge badge-pulse">● {Math.round(e.progress || 0)}%</span>}
        {isFailed && <span className="badge" style={{ background: 'var(--err)', color: '#fff' }}>ошибка</span>}
        {isReady && (
          <button className="play-pill" onClick={(ev) => { ev.stopPropagation(); onOpen(); }}>
            {Icons.play}
          </button>
        )}
      </div>

      <div className="ep-body">
        <div className="ep-title">
          {editing ? (
            <input
              autoFocus
              className="text-input ep-title-input"
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onClick={(ev) => ev.stopPropagation()}
              onBlur={commitRename}
              onKeyDown={(ev) => {
                ev.stopPropagation();
                if (ev.key === 'Enter') ev.target.blur();
                if (ev.key === 'Escape') { setDraft(e.title); setEditing(false); }
              }}
            />
          ) : (
            <div className="ep-title-es ep-title-renamable" onClick={(ev) => { ev.stopPropagation(); setEditing(true); }} title="нажми чтобы переименовать">
              {e.title}
              <span className="ep-title-edit-hint">✎</span>
            </div>
          )}
          {e.title_ru && <div className="ep-title-ru">{e.title_ru}</div>}
        </div>
        <div className="ep-meta">
          {e.recorded_at ? formatDateRu(e.recorded_at) : relativeTime(e.created_at)}
          {e.filesize_bytes ? ` · ${formatBytes(e.filesize_bytes)}` : ''}
        </div>

        {isReady && (
          <>
            <div className="ep-stats">
              <span className="chip">{e.chapters_count || 0} {ruPlural(e.chapters_count || 0, 'тема', 'темы', 'тем')}</span>
              <span className="chip">{e.clips_count || 0} {ruPlural(e.clips_count || 0, 'фрагмент', 'фрагмента', 'фрагментов')}</span>
              {e.published_count > 0 && e.published_count === e.clips_count && <span className="chip chip-hue-sage">✓ всё опубл.</span>}
              {e.published_count > 0 && e.published_count < e.clips_count && <span className="chip chip-hue-amber">✓ {e.published_count} опубл.</span>}
            </div>
            <div className="ep-actions">
              <button className="btn btn-dark btn-full" onClick={(ev) => { ev.stopPropagation(); onOpen(); }}>открыть →</button>
            </div>
          </>
        )}

        {isProcessing && (
          <>
            <div className="ringbar"><div className="ringbar-fill" style={{ width: Math.round(e.progress || 0) + '%' }} /></div>
            <div className="ep-meta">{e.status_message || 'обрабатываю…'}</div>
          </>
        )}

        {isFailed && (
          <>
            <div className="ep-meta" style={{ color: 'var(--err)' }}>{e.status_message || 'не удалось обработать'}</div>
            <div className="ep-actions">
              <button className="btn btn-dark btn-full" onClick={async (ev) => {
                ev.stopPropagation();
                try {
                  await api.retryEpisode(e.id);
                } catch (err) { /* toast handled elsewhere */ }
              }}>↻ повторить</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReelCard({ r, onOpen }) {
  const isProcessing = ['pending', 'downloading', 'transcribing', 'analyzing'].includes(r.status);
  return (
    <div className="reel-card" onClick={onOpen} role="button">
      <div className="reel-thumb">
        <VideoThumb aspect="9/16" src={r.thumbnail_url}
          label={isProcessing ? 'обработка…' : r.title} />
        {r.duration_seconds && <span className="badge badge-dark">{formatDurationHm(r.duration_seconds)}</span>}
        {isProcessing && <span className="badge badge-pulse">● {Math.round(r.progress || 0)}%</span>}
      </div>
      <div className="reel-body">
        <div className="story-title-es">{r.title}</div>
        {r.title_ru && <div className="story-title-ru">{r.title_ru}</div>}
        <div className="story-meta">{relativeTime(r.created_at)} · <StatusLabel status={r.status} /></div>
      </div>
    </div>
  );
}
